<template>
  <v-flex
    id="credits"
    class="question"
  >
    <p class="mb-0 text-uppercase">
      {{ $t('sheet.sheetOne.hint_material') }}
    </p>
    <h2 class="display-3 display-3_dark">
      {{ getCreditsQuestions?.label }}
    </h2>
    <ol class="questions-list">
      <li
        v-if="getCreditsQuestions.hasOwnProperty('has_credits')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getCreditsQuestions.has_credits.required }"
        >
          {{ getCreditsQuestions.has_credits.label }}
        </p>
        <ConfirmButtons
          v-model="hasCredits"
          v-validate="{ required: getCreditsQuestions.has_credits.required }"
          string
          :error-messages="errors.collect('hasCredits')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'hasCredits'"
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="hasCredits === 'yes' && getCreditsQuestions.hasOwnProperty('credit_reason')"
        class="mt-4"
      >
        <p
          class="header custom lA"
          :class="{ required: getCreditsQuestions.credit_reason.required }"
        >
          {{ getCreditsQuestions.credit_reason.label }}
        </p>
        <v-textarea
          v-model="creditReason"
          v-validate="{ ...regularLongText(getCreditsQuestions.credit_reason.required) }"
          :placeholder="$t('placeholders.add_text')"
          :error-messages="errors.collect('creditReason')"
          :data-vv-as="$t('label.question')"
          data-vv-name="creditReason"
          :required="getCreditsQuestions.credit_reason.required"
          :disabled="readOnly"
          auto-grow
        />
        <div v-if="getCreditsQuestions.hasOwnProperty('credit_for_decent_live_conditions')">
          <p class="mt-4">
            {{ getCreditsQuestions.credit_for_decent_live_conditions.label }}
          </p>
          <ConfirmButtons
            v-model="creditForDecentLivingConditions"
            v-validate="{
              required: getCreditsQuestions.credit_for_decent_live_conditions.required
            }"
            string
            :error-messages="errors.collect('creditForDecentLivingConditions')"
            :data-vv-as="$t('label.question')"
            :data-vv-name="'creditForDecentLivingConditions'"
            :disabled="readOnly"
          />
        </div>
        <p
          v-if="creditReason === 70 || creditReason === 71"
          class="f-14 mb-0"
        >
          {{ $t('sheet.sheetOne.meeting.credits.credit_reason.info') }}
        </p>
        <p
          v-if="creditReason === credits.OTHER_CREDIT_REASON"
          class="f-14 mb-0"
        >
          {{ $t('sheet.sheetOne.meeting.credits.credit_reason.optional') }}
        </p>
        <CreditForm
          v-if="getCreditsQuestions.hasOwnProperty('credit_details')"
          class="mt-4"
          :read-only="readOnly"
        />
      </li>
      <li
        v-if="hasCredits === 'yes' && getCreditsQuestions.hasOwnProperty('regular_credit_repair')"
        class="mt-4"
      >
        <p
          class="header custom lB"
          :class="{ required: getCreditsQuestions.regular_credit_repair.required }"
        >
          {{ getCreditsQuestions.regular_credit_repair.label }}
        </p>
        <ConfirmButtons
          v-model="regularCreditRepair"
          v-validate="{ required: getCreditsQuestions.regular_credit_repair.required }"
          string
          :error-messages="errors.collect('regularCreditRepair')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'regularCreditRepair'"
          :disabled="readOnly"
        />
        <p
          v-if="regularCreditRepair === 'no' && creditReason !== credits.OTHER_CREDIT_REASON"
          class="f-14 mb-0 mt-1"
        >
          {{ $t('sheet.sheetOne.meeting.credits.credit_reason.optional') }}
        </p>
      </li>
      <li
        v-if="hasCredits === 'yes'"
        class="mt-4"
      >
        <MoneySummary
          :title="$t('sheet.sheetOne.meeting.credits.credit_summary')"
          :value="isNaN(totalAmount) ? 0 : totalAmount"
        />
      </li>
      <li
        v-if="getCreditsQuestions.hasOwnProperty('has_informal_liability')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getCreditsQuestions.has_informal_liability.required }"
        >
          {{ getCreditsQuestions.has_informal_liability.label }}
        </p>
        <ConfirmButtons
          v-model="hasInformalLiability"
          v-validate="{ required: getCreditsQuestions.has_informal_liability.required }"
          string
          :error-messages="errors.collect('hasInformalLiability')"
          data-vv-as=" "
          :data-vv-name="'hasInformalLiability'"
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="
          hasInformalLiability === 'yes' &&
            getCreditsQuestions.hasOwnProperty('informal_liability_description')
        "
        class="mt-4"
      >
        <p
          class="fw-600"
          :class="{ required: getCreditsQuestions.informal_liability_description.required }"
        >
          {{ getCreditsQuestions.informal_liability_description.label }}
        </p>
        <v-textarea
          v-model="informalLiabilityDescription"
          v-validate="{
            ...requiredRegularField(getCreditsQuestions.informal_liability_description.required)
          }"
          :placeholder="$t('placeholders.add_text')"
          :error-messages="errors.collect('informalLiabilityDescription')"
          data-vv-as=" "
          data-vv-name="informalLiabilityDescription"
          :required="getCreditsQuestions.informal_liability_description.required"
          class="small"
          auto-grow
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="
          hasInformalLiability === 'yes' &&
            getCreditsQuestions.hasOwnProperty('informal_liability_reason')
        "
        class="mt-4"
      >
        <p
          class="header custom lA"
          :class="{ required: getCreditsQuestions.informal_liability_reason.required }"
        >
          {{ getCreditsQuestions.informal_liability_reason.label }}
        </p>
        <v-textarea
          v-model="informalLiabilityReason"
          v-validate="{
            ...regularLongText(getCreditsQuestions.informal_liability_reason.required)
          }"
          :placeholder="$t('placeholders.add_text')"
          :error-messages="errors.collect('informalLiabilityReason')"
          :data-vv-as="$t('label.question')"
          data-vv-name="informalLiabilityReason"
          :required="getCreditsQuestions.informal_liability_reason.required"
          :disabled="readOnly"
          auto-grow
        />
        <div
          v-if="
            getCreditsQuestions.hasOwnProperty('informal_liability_for_decent_living_conditions')
          "
        >
          <p class="mt-4">
            {{ getCreditsQuestions.informal_liability_for_decent_living_conditions.label }}
          </p>
          <ConfirmButtons
            v-model="informalLiabilityForDecentLivingConditions"
            v-validate="{
              required: getCreditsQuestions.informal_liability_for_decent_living_conditions.required
            }"
            string
            :error-messages="errors.collect('informalLiabilityForDecentLivingConditions')"
            :data-vv-as="$t('label.question')"
            :data-vv-name="'informalLiabilityForDecentLivingConditions'"
            :disabled="readOnly"
          />
        </div>
        <v-radio-group
          v-model="informalLiabilityReason"
          v-validate="{ required: getCreditsQuestions.informal_liability_reason.required }"
          :error-messages="errors.collect('informalLiabilityReason')"
          data-vv-as=" "
          :data-vv-name="'informalLiabilityReason'"
          :disabled="readOnly"
        >
          <v-radio
            v-for="(res, index) in informalLiabilityReasonsList"
            :key="index"
            :label="res.description"
            :value="res.id"
          />
        </v-radio-group>
        <v-flex
          v-if="
            informalLiabilityReason === credits.OTHER_INFORMAL_LIABILITY_REASON &&
              getCreditsQuestions.informal_liability_reason.hasOwnProperty('informal_liability_other')
          "
          xs12
        >
          <p
            class="fw-600"
            :class="{
              required:
                getCreditsQuestions.informal_liability_reason.informal_liability_other.required
            }"
          >
            {{ getCreditsQuestions.informal_liability_reason.informal_liability_other.label }}
          </p>
          <v-textarea
            v-model="informalLiabilityReasonOther"
            v-validate="{
              ...requiredRegularField(
                getCreditsQuestions.informal_liability_reason.informal_liability_other.required
              )
            }"
            :placeholder="$t('placeholders.add_text')"
            :error-messages="errors.collect('informalLiabilityReasonOther')"
            data-vv-as=" "
            data-vv-name="informalLiabilityReasonOther"
            :required="
              getCreditsQuestions.informal_liability_reason.informal_liability_other.required
            "
            class="small"
            auto-grow
            :disabled="readOnly"
          />
          <p class="f-14 mt-2 mb-0">
            {{ getCreditsQuestions.informal_liability_reason.informal_liability_other.sub_label }}
          </p>
        </v-flex>
        <InformalLiabilityForm
          v-if="
            getCreditsQuestions.informal_liability_reason.hasOwnProperty(
              'informal_liability_details'
            )
          "
          :read-only="readOnly"
        />
      </li>
      <li
        v-if="
          hasInformalLiability === 'yes' &&
            getCreditsQuestions.hasOwnProperty('informal_liability_pending')
        "
        class="mt-4"
      >
        <p
          class="header custom lB"
          :class="{ required: getCreditsQuestions.informal_liability_pending.required }"
        >
          {{ getCreditsQuestions.informal_liability_pending.label }}
        </p>
        <ConfirmButtons
          v-model="informalLiabilityPending"
          v-validate="{ required: getCreditsQuestions.informal_liability_pending.required }"
          string
          :error-messages="errors.collect('informalLiabilityPending')"
          data-vv-as=" "
          :data-vv-name="'informalLiabilityPending'"
          :disabled="readOnly"
        />
        <p
          v-if="
            getCreditsQuestions.informal_liability_reason.hasOwnProperty(
              'informal_liability_other'
            ) &&
              informalLiabilityPending === 'no' &&
              informalLiabilityReason !== 367
          "
          class="f-14 mt-2 mb-0"
        >
          {{ getCreditsQuestions.informal_liability_reason.informal_liability_other.sub_label }}
        </p>
      </li>
      <li
        v-if="hasInformalLiability === 'yes'"
        class="mt-4"
      >
        <MoneySummary
          :title="$t('sheet.sheetOne.meeting.credits.liability_summary')"
          :value="isNaN(informalLiabilityTotalAmount) ? 0 : informalLiabilityTotalAmount"
        />
      </li>
      <li
        v-if="getCreditsQuestions.hasOwnProperty('has_arrears')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getCreditsQuestions.has_arrears.required }"
        >
          {{ getCreditsQuestions.has_arrears.label }}
        </p>
        <ConfirmButtons
          v-model="hasArrears"
          v-validate="{ required: getCreditsQuestions.has_arrears.required }"
          string
          :error-messages="errors.collect('hasArrears')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'hasArrears'"
          :disabled="readOnly"
        />
      </li>
      <li
        v-if="hasArrears === 'yes' && getCreditsQuestions.hasOwnProperty('type_of_arrears')"
        class="mt-4"
      >
        <p
          class="header mt-4 custom lA"
          :class="{ required: getCreditsQuestions.type_of_arrears.required }"
        >
          {{ getCreditsQuestions.type_of_arrears.label }}
        </p>
        <v-checkbox
          v-for="(item, index) in typeOfArrearsResources"
          :key="index"
          v-model="typeOfArrears"
          v-validate="{ required: getCreditsQuestions.type_of_arrears.required }"
          :value="item?.id"
          :label="item?.description"
          :error="errors.has('typeOfArrears')"
          :error-messages="
            index + 1 === typeOfArrearsResources.length ? errors.collect('typeOfArrears') : null
          "
          data-vv-as=" "
          :data-vv-name="'typeOfArrears'"
          type="checkbox"
          off-icon=""
          on-icon="check"
          multiple
          :disabled="readOnly"
        />
        <div
          v-if="
            isArrearsOtherChosen &&
              getCreditsQuestions?.type_of_arrears?.hasOwnProperty('arrears_other')
          "
          class="mt-3"
        >
          <p>{{ getCreditsQuestions.type_of_arrears.arrears_other.label }}</p>
          <v-textarea
            v-model="arrearsOther"
            v-validate="{
              ...regularLongText(getCreditsQuestions.type_of_arrears.arrears_other.required, 180)
            }"
            :placeholder="$t('placeholders.add_text')"
            :error-messages="errors.collect('arrearsOther')"
            data-vv-as=" "
            data-vv-name="arrearsOther"
            :required="getCreditsQuestions.type_of_arrears.arrears_other.required"
            class="small"
            :disabled="readOnly"
            auto-grow
          />
        </div>
        <v-layout
          align-center
          class="mt-2"
        >
          <v-flex
            xs10
            md7
            lg3
          >
            <v-text-field
              v-model="arrearsAmount"
              v-validate="{ ...requiredMoney(getCreditsQuestions.arrears_amount.required) }"
              :label="getCreditsQuestions.arrears_amount.label"
              :error-messages="errors.collect('arrears_amount')"
              :data-vv-as="$t('sheet.sheetOne.meeting.credits.labels.arrears_amount')"
              :data-vv-name="'arrears_amount'"
              :required="getCreditsQuestions.arrears_amount.required"
              class="pt-0 mt-3"
              :disabled="readOnly"
              inputmode="decimal"
            />
          </v-flex>
          <v-flex
            xs2
            md5
            lg2
            shrink
            justify-center
            ml-3
          >
            <v-subheader class="px-0 pt-1 required">
              {{ $t('label.currency') }}
            </v-subheader>
          </v-flex>
        </v-layout>
      </li>
      <li
        v-if="getCreditsQuestions.hasOwnProperty('has_law_problems')"
        class="mt-4"
      >
        <p
          class="header"
          :class="{ required: getCreditsQuestions.has_law_problems.required }"
        >
          {{ getCreditsQuestions.has_law_problems.label }}
        </p>
        <ConfirmButtons
          v-model="hasLawProblems"
          v-validate="{ required: getCreditsQuestions.has_law_problems.required }"
          string
          :error-messages="errors.collect('hasLawProblems')"
          :data-vv-as="$t('label.question')"
          :data-vv-name="'hasLawProblems'"
          :disabled="readOnly"
        />
      </li>
    </ol>
  </v-flex>
</template>

<script>
import ConfirmButtons from '@/components/widgets/sheet/ConfirmButtons';
import CreditForm from '@/components/widgets/sheet/questions/credits/CreditForm';
import MoneySummary from '@/components/widgets/sheet/moneySummary';
import InformalLiabilityForm from '@/components/widgets/sheet/questions/credits/InformalLiabilityForm';

import credits from '@/constants/sheet/credits';

import formatDecimalVariables from '@/mixins/formatDecimalVariables';
import sheetValidationRules from '@/mixins/sheet/sheetValidationRules';
import sheetResourcesGetter from '@/mixins/sheet/sheetResourcesGetter';

import { mapGetters } from 'vuex';
import { createHelpers } from 'vuex-map-fields';

const { mapFields } = createHelpers({
    getterType: 'sheet/getCreditsField',
    mutationType: 'sheet/updateCreditsField'
});

export default {
    name: 'QuestionsCredits',
    components: {
        ConfirmButtons,
        CreditForm,
        MoneySummary,
        InformalLiabilityForm
    },
    mixins: [formatDecimalVariables, sheetValidationRules, sheetResourcesGetter],
    inject: ['$validator'],
    props: {
        readOnly: {
            default: false,
            type: Boolean
        },
        isCurrentEdition: {
            type: Boolean,
            default: true
        }
    },
    data () {
        return {
            credits
        };
    },
    computed: {
        ...mapFields([
            'hasCredits',
            'creditReason',
            'hasLawProblems',
            'regularCreditRepair',
            'hasArrears',
            'typeOfArrears',
            'arrearsAmount',
            'totalAmount',
            'creditDetails',
            'creditReasonExplanation',
            'hasInformalLiability',
            'informalLiabilityDescription',
            'informalLiabilityReason',
            'informalLiabilityPending',
            'informalLiabilityDetails',
            'informalLiabilityTotalAmount',
            'informalLiabilityReasonOther',
            'informalLiabilityForDecentLivingConditions',
            'creditForDecentLivingConditions',
            'arrearsOther'
        ]),
        ...mapGetters('sheet', ['getCreditsQuestions', 'getLawyersPackQuestions']),
        creditReasonList () {
            return this.getResources(this.getCreditsQuestions.credit_reason);
        },
        informalLiabilityReasonsList () {
            return this.getResources(this.getCreditsQuestions.informal_liability_reason);
        },
        typeOfArrearsResources () {
            return this.getResources(this.getCreditsQuestions?.type_of_arrears) ?? [];
        },
        isArrearsOtherChosen () {
            const OTHER_ARREARS_ID = 641;
            return !!this.typeOfArrears?.includes(OTHER_ARREARS_ID);
        },
        computedTotalAmount () {
            if (
                this.hasCredits === 'no' ||
        this.regularCreditRepair === 'no' ||
        this.creditForDecentLivingConditions === 'no' ||
        this.errors?.items?.find((el) => el?.field === 'month_credit_amount')
            ) {
                return 0;
            } else {
                return this.formatDecimalVariable(this.creditDetails.monthCreditAmount);
            }
        },
        computedInformalLiabilityTotalAmount () {
            if (
                this.hasInformalLiability === 'no' ||
        this.informalLiabilityPending === 'no' ||
        this.informalLiabilityForDecentLivingConditions === 'no' ||
        this.errors?.items?.find((el) => el?.field === 'month_liability_amount')
            ) {
                return 0;
            } else {
                return this.formatDecimalVariable(this.informalLiabilityDetails.monthLiabilityAmount);
            }
        }
    },
    watch: {
        isArrearsOtherChosen () {
            this.arrearsOther = null;
        },
        creditReason: {
            immediate: true,
            handler (val) {
                if (val !== this.credits.OTHER_CREDIT_REASON) {
                    this.creditReasonExplanation = null;
                }
            }
        },
        informalLiabilityReason: {
            immediate: true,
            handler (val) {
                if (val !== this.credits.OTHER_INFORMAL_LIABILITY_REASON) {
                    this.informalLiabilityReasonOther = null;
                }
            }
        },
        hasArrears (val) {
            if (val === 'no') {
                this.arrearsAmount = null;
                this.typeOfArrears = null;
            }
        },
        hasLawProblems: {
            immediate: true,
            handler (event) {
                if (this.isCurrentEdition) return;
                if (this.getLawyersPackQuestions && event === 'yes') {
                    this.$store.commit('sheet/removeHiddenSelector', 'lawyersPack');
                } else if (event === 'no') {
                    this.$store.commit('sheet/addHiddenSelector', 'lawyersPack');
                }
            }
        },
        hasCredits (val) {
            if (val === 'no') {
                this.$nextTick(() => {
                    this.creditReason = [];
                    this.regularCreditRepair = null;
                    this.creditForDecentLivingConditions = null;
                });
            }
        },
        computedTotalAmount (val) {
            this.totalAmount = val;
        },
        computedInformalLiabilityTotalAmount (val) {
            this.informalLiabilityTotalAmount = val;
        },
        hasInformalLiability: {
            handler (val) {
                if (val === 'no') {
                    this.$nextTick(() => {
                        this.informalLiabilityReason = null;
                        this.informalLiabilityPending = null;
                        this.informalLiabilityDescription = null;
                        this.informalLiabilityForDecentLivingConditions = null;
                    });
                }
            }
        }
    }
};
</script>

<style scoped></style>
